import { computed, ref } from 'vue';

// --- Types ---

export type LiftPlanEntryColumnKey =
  | 'number'
  | 'description'
  | 'dueDate'
  | 'amount'
  | 'retention'
  | 'amountDue'
  | 'invoicedDate'
  | 'paymentDate'
  | 'comment';

export type LiftPlanSubtotalColumnKey =
  | 'label'
  | 'amount'
  | 'invoicedDate'
  | 'paymentDate'
  | 'comment';

export interface LiftPlanEntry {
  id: string;
  number: number;
  description?: string;
  dueDate?: Date;
  amount: number;
  retention: number;
  amountDue: number;
  invoicedDate?: Date;
  paymentDate?: Date;
  comment?: string;
}

export interface LiftPlanSubtotalRow {
  id: string;
  label: string;
  amount: number;
  invoicedDate?: Date;
  paymentDate?: Date;
  comment?: string;
}

export interface LiftPlan {
  id: string;
  name: string;
  contractSum: number;
  retentionPercentage: number;
  totalAmount: number;
  totalRetention: number;
  totalAmountDue: number;
  entreprenadsumma: number;
  subtotalRows: LiftPlanSubtotalRow[];
  entries: LiftPlanEntry[];
}

export type SaveLiftPlanData = Omit<
  LiftPlan,
  'totalAmount' | 'totalRetention' | 'totalAmountDue' | 'entreprenadsumma'
>;

export type LiftPlanColumnVisibility = 'visible' | 'hidden';

// --- Constants ---

export const ALL_COLUMN_KEYS: LiftPlanEntryColumnKey[] = [
  'number',
  'description',
  'dueDate',
  'amount',
  'retention',
  'amountDue',
  'invoicedDate',
  'paymentDate',
  'comment',
];

// --- Dispatch action union ---

type LiftPlanToggleEditEvent = { type: 'toggle-edit' };
type LiftPlanCancelEvent = { type: 'cancel' };
type LiftPlanUpdateNameEvent = { type: 'update-name'; value: string };
type LiftPlanDeleteEvent = { type: 'delete' };
type LiftPlanAddEntryEvent = { type: 'add-entry' };
type LiftPlanDeleteEntryEvent = { type: 'delete-entry'; id: string };
type LiftPlanUpdateEntryEvent = {
  type: 'update-entry';
  id: string;
  field: LiftPlanEntryColumnKey;
  value: string;
};
type LiftPlanAddSubtotalEvent = { type: 'add-subtotal' };
type LiftPlanDeleteSubtotalEvent = { type: 'delete-subtotal'; id: string };
type LiftPlanUpdateSubtotalEvent = {
  type: 'update-subtotal';
  id: string;
  field: LiftPlanSubtotalColumnKey;
  value: string;
};
type LiftPlanUpdatePlanEvent = {
  type: 'update-plan';
  field: 'contractSum' | 'retentionPercentage' | 'retentionAmount';
  value: string;
};
type LiftPlanSetColumnStateEvent = {
  type: 'set-column-state';
  key: LiftPlanEntryColumnKey;
  state: 'visible' | 'hidden';
};

export type LiftPlanActionEvent =
  | LiftPlanToggleEditEvent
  | LiftPlanCancelEvent
  | LiftPlanUpdateNameEvent
  | LiftPlanDeleteEvent
  | LiftPlanAddEntryEvent
  | LiftPlanDeleteEntryEvent
  | LiftPlanUpdateEntryEvent
  | LiftPlanAddSubtotalEvent
  | LiftPlanDeleteSubtotalEvent
  | LiftPlanUpdateSubtotalEvent
  | LiftPlanUpdatePlanEvent
  | LiftPlanSetColumnStateEvent;

// --- Factory ---

export const createLiftPlanStore = (
  initialLiftPlan: Omit<
    LiftPlan,
    'totalAmount' | 'totalRetention' | 'totalAmountDue' | 'entreprenadsumma'
  >,
) => {
  const states = ref({
    isEditing: false,
    columnStates: Object.fromEntries(
      ALL_COLUMN_KEYS.map((k) => [k, 'visible' as LiftPlanColumnVisibility]),
    ) as Record<LiftPlanEntryColumnKey, LiftPlanColumnVisibility>,
    liftPlan: {
      ...initialLiftPlan,
      entries: initialLiftPlan.entries.map((e) => ({ ...e })),
      subtotalRows: initialLiftPlan.subtotalRows.map((r) => ({ ...r })),
    },
  });
  // Internal helpers

  function recalcEntry(
    entry: (typeof states.value.liftPlan.entries)[number],
    pct: number,
  ): void {
    entry.retention = Math.round(entry.amount * (pct / 100));
    entry.amountDue = entry.amount - entry.retention;
  }

  // Snapshot for cancel/restore

  type Snapshot = {
    name: string;
    contractSum: number;
    retentionPercentage: number;
    subtotalRows: typeof states.value.liftPlan.subtotalRows;
    entries: typeof states.value.liftPlan.entries;
  };

  const snapshot = ref<Snapshot | null>(null);

  // Dispatch — sole mutation path

  function dispatch(event: LiftPlanActionEvent): void {
    switch (event.type) {
      case 'toggle-edit': {
        if (!states.value.isEditing) {
          snapshot.value = {
            name: states.value.liftPlan.name,
            contractSum: states.value.liftPlan.contractSum,
            retentionPercentage: states.value.liftPlan.retentionPercentage,
            subtotalRows: states.value.liftPlan.subtotalRows.map((r) => ({
              ...r,
            })),
            entries: states.value.liftPlan.entries.map((e) => ({ ...e })),
          };
        } else {
          snapshot.value = null;
        }
        states.value.isEditing = !states.value.isEditing;
        break;
      }

      case 'cancel': {
        if (snapshot.value) {
          states.value.liftPlan.name = snapshot.value.name;
          states.value.liftPlan.contractSum = snapshot.value.contractSum;
          states.value.liftPlan.retentionPercentage =
            snapshot.value.retentionPercentage;
          states.value.liftPlan.subtotalRows.splice(
            0,
            states.value.liftPlan.subtotalRows.length,
            ...snapshot.value.subtotalRows,
          );
          states.value.liftPlan.entries.splice(
            0,
            states.value.liftPlan.entries.length,
            ...snapshot.value.entries,
          );
        }
        snapshot.value = null;
        states.value.isEditing = false;
        break;
      }

      case 'delete': {
        // No local state mutation — forwarded to parent via emit
        break;
      }

      case 'update-name': {
        states.value.liftPlan.name = event.value;
        break;
      }

      case 'add-entry': {
        const nextNumber =
          states.value.liftPlan.entries.length > 0
            ? Math.max(...states.value.liftPlan.entries.map((e) => e.number)) +
              1
            : 1;
        states.value.liftPlan.entries.push({
          id: Date.now().toString(),
          number: nextNumber,
          dueDate: undefined,
          amount: 0,
          retention: 0,
          amountDue: 0,
          invoicedDate: undefined,
          paymentDate: undefined,
          comment: undefined,
        });
        break;
      }

      case 'delete-entry': {
        const idx = states.value.liftPlan.entries.findIndex(
          (e) => e.id === event.id,
        );
        if (idx !== -1) states.value.liftPlan.entries.splice(idx, 1);
        break;
      }

      case 'update-entry': {
        const entry = states.value.liftPlan.entries.find(
          (e) => e.id === event.id,
        );
        if (!entry) break;
        switch (event.field) {
          case 'amount': {
            const val = parseInt(event.value.replace(/\D/g, ''), 10);
            if (isNaN(val)) break;
            entry.amount = val;
            const retentionCap = Math.round(
              states.value.liftPlan.contractSum *
                (states.value.liftPlan.retentionPercentage / 100),
            );
            const alreadyRetained = states.value.liftPlan.entries
              .filter((e) => e.id !== entry.id)
              .reduce((sum, e) => sum + e.retention, 0);
            const remaining = Math.max(0, retentionCap - alreadyRetained);
            entry.retention = Math.min(Math.round(val * 0.1), remaining);
            entry.amountDue = val - entry.retention;
            break;
          }
          case 'retention': {
            const val = parseInt(event.value.replace(/\D/g, ''), 10);
            if (isNaN(val)) break;
            entry.retention = val;
            entry.amountDue = entry.amount - val;
            break;
          }
          case 'amountDue': {
            const val = parseInt(event.value.replace(/\D/g, ''), 10);
            if (isNaN(val)) break;
            entry.amountDue = val;
            entry.retention = entry.amount - val;
            break;
          }
          case 'dueDate':
            entry.dueDate = event.value ? new Date(event.value) : undefined;
            break;
          case 'invoicedDate':
            entry.invoicedDate = event.value
              ? new Date(event.value)
              : undefined;
            break;
          case 'paymentDate':
            entry.paymentDate = event.value ? new Date(event.value) : undefined;
            break;
          case 'description':
            entry.description = event.value || undefined;
            break;
          case 'comment':
            entry.comment = event.value || undefined;
            break;
        }
        break;
      }

      case 'update-plan': {
        switch (event.field) {
          case 'retentionPercentage': {
            const pct = parseFloat(
              event.value.replace(/\s/g, '').replace(',', '.'),
            );
            if (isNaN(pct)) break;
            states.value.liftPlan.retentionPercentage = pct;
            for (const entry of states.value.liftPlan.entries) {
              recalcEntry(entry, pct);
            }
            break;
          }
        }
        break;
      }

      case 'add-subtotal': {
        states.value.liftPlan.subtotalRows.push({
          id: Date.now().toString(),
          label: '',
          amount: 0,
        });
        break;
      }

      case 'delete-subtotal': {
        const idx = states.value.liftPlan.subtotalRows.findIndex(
          (r) => r.id === event.id,
        );
        if (idx !== -1) states.value.liftPlan.subtotalRows.splice(idx, 1);
        break;
      }

      case 'update-subtotal': {
        const row = states.value.liftPlan.subtotalRows.find(
          (r) => r.id === event.id,
        );
        if (!row) break;
        switch (event.field) {
          case 'label':
            row.label = event.value;
            break;
          case 'amount': {
            const val = parseInt(event.value.replace(/\D/g, ''), 10);
            if (!isNaN(val)) row.amount = val;
            break;
          }
          case 'invoicedDate':
            row.invoicedDate = event.value ? new Date(event.value) : undefined;
            break;
          case 'paymentDate':
            row.paymentDate = event.value ? new Date(event.value) : undefined;
            break;
          case 'comment':
            row.comment = event.value || undefined;
            break;
        }
        break;
      }

      case 'set-column-state': {
        states.value.columnStates[event.key] = event.state;
        break;
      }
    }
  }

  const isDirty = computed(() => {
    if (!snapshot.value) return false;
    const current = {
      name: states.value.liftPlan.name,
      contractSum: states.value.liftPlan.contractSum,
      retentionPercentage: states.value.liftPlan.retentionPercentage,
      entries: states.value.liftPlan.entries,
      subtotalRows: states.value.liftPlan.subtotalRows,
    };
    return JSON.stringify(current) !== JSON.stringify(snapshot.value);
  });

  const totalAmount = computed(() =>
    states.value.liftPlan.entries.reduce((sum, e) => sum + e.amount, 0),
  );
  const totalRetention = computed(() =>
    states.value.liftPlan.entries.reduce((sum, e) => sum + e.retention, 0),
  );
  const totalAmountDue = computed(() =>
    states.value.liftPlan.entries.reduce((sum, e) => sum + e.amountDue, 0),
  );
  const entreprenadsumma = computed(
    () =>
      totalAmount.value +
      totalRetention.value +
      states.value.liftPlan.subtotalRows.reduce((s, r) => s + r.amount, 0),
  );

  const state = computed(() => ({
    isEditing: states.value.isEditing,
    liftPlan: {
      ...states.value.liftPlan,
      totalAmount: totalAmount.value,
      totalRetention: totalRetention.value,
      totalAmountDue: totalAmountDue.value,
      entreprenadsumma: entreprenadsumma.value,
    },
    columnStates: states.value.columnStates,
    isDirty: isDirty.value,
  }));

  return { state, dispatch };
};
